import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";
import BrandToolbar from "@/components/BrandToolbar";
import {
  Home,
  Lock,
  MessageSquareHeart,
  Users,
  HeartPulse,
  Settings,
  LogOut,
  Menu,
  X,
  Sparkles,
  Database,
  Scroll,
  FileText,
} from "lucide-react";

const PLAN_LABEL = {
  free: "Gratuito",
  personal: "Personal",
  family: "Familiar",
  premium: "Premium",
};

function NavItems({ onClick, mobile = false }) {
  const { t } = useI18n();
  const items = [
    { to: "/dashboard", icon: Home, k: "nav_dashboard" },
    { to: "/vault", icon: Lock, k: "nav_vault" },
    { to: "/digital-assets", icon: Database, k: "nav_assets" },
    { to: "/messages", icon: MessageSquareHeart, k: "nav_messages" },
    { to: "/heirs", icon: Users, k: "nav_heirs" },
    { to: "/will-executor", icon: Scroll, k: "nav_will" },
    { to: "/ice", icon: FileText, k: "nav_ice" },
    { to: "/proof-of-life", icon: HeartPulse, k: "nav_pol" },
    { to: "/settings", icon: Settings, k: "nav_settings" },
  ];
  // Mobile items get larger padding, bigger icons and bigger type for easier tapping
  const itemClass = mobile
    ? "flex items-center gap-4 px-5 py-4 rounded-md text-[15px] transition-all"
    : "flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-all";
  const iconSize = mobile ? "w-5 h-5" : "w-4 h-4";
  return (
    <nav className={mobile ? "flex-1 p-3 space-y-1.5" : "flex-1 p-4 space-y-1"}>
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/dashboard"}
            onClick={onClick}
            data-testid={`nav-${it.to.replace(/^\//, "") || "dashboard"}`}
            className={({ isActive }) =>
              `${itemClass} ${
                isActive
                  ? "nav-active"
                  : "text-muted hover:text-gold-light hover:bg-white/[0.02]"
              }`
            }
          >
            <Icon className={iconSize} strokeWidth={2} />
            {t(it.k)}
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserPanel({ onNav }) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const plan = user?.plan || "free";
  return (
    <div className="p-4 border-t border-gold-subtle space-y-3">
      <Link
        to="/plans"
        onClick={onNav}
        className="block px-3 py-2 rounded-md border border-gold-subtle hover:border-gold transition group"
        data-testid="sidebar-plan-badge"
      >
        <div className="font-label text-faded mb-1 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-gold" /> {t("plan_current")}
        </div>
        <div className="text-gold-light text-sm group-hover:text-gold mt-1">
          {PLAN_LABEL[plan] || plan}
        </div>
      </Link>
      <div className="flex items-center gap-3 px-2">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold text-xs">
            {(user?.name || "K").charAt(0)}
          </div>
        )}
        <div className="text-xs min-w-0">
          <div className="text-gold-light truncate">{user?.name}</div>
          <div className="text-faded truncate">{user?.email}</div>
        </div>
      </div>
      <button
        onClick={logout}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted hover:text-gold-light transition"
        data-testid="logout-btn"
      >
        <LogOut className="w-3.5 h-3.5" /> {t("nav_logout")}
      </button>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const [drawer, setDrawer] = useState(false);
  const closeDrawer = () => setDrawer(false);

  return (
    <div className="min-h-screen-safe flex bg-obsidian text-white">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col border-r border-gold-subtle bg-obsidian-2/40 backdrop-blur-xl"
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-gold-subtle">
          <Logo size={28} />
        </div>
        <NavItems />
        <UserPanel />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeDrawer}
          />
          <aside className="relative w-[88vw] max-w-[340px] flex flex-col bg-obsidian-2 border-r border-gold-subtle h-full">
            <div className="p-6 border-b border-gold-subtle flex items-center justify-between">
              <Logo size={30} />
              <button onClick={closeDrawer} className="text-muted hover:text-gold p-2 -mr-2" data-testid="drawer-close" aria-label="Cerrar menú">
                <X className="w-6 h-6" />
              </button>
            </div>
            <NavItems onClick={closeDrawer} mobile />
            <UserPanel onNav={closeDrawer} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden glass-header pt-safe-or-3 px-2 pb-3 flex items-center justify-between gap-1 sticky top-0 z-30">
          <button
            onClick={() => setDrawer(true)}
            className="text-gold p-2 -ml-1 shrink-0"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Logo size={22} className="shrink min-w-0" />
          <BrandToolbar className="shrink-0 brand-toolbar-compact" />
        </header>
        {/* Desktop top bar */}
        <header className="hidden md:flex justify-end items-center gap-4 px-8 py-5 border-b border-gold-subtle bg-obsidian-2/30">
          <BrandToolbar />
        </header>

        <main className="flex-1 p-5 md:p-10 max-w-7xl w-full mx-auto fade-in pb-safe-or-3">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
